<template>
  <div class="enhanced-trim-comparison">
    <div class="comparison-controls">
      <v-row align="center" no-gutters>
        <v-col cols="auto">
          <v-btn-toggle
            v-model="showOnlyDifferences"
            mandatory
            density="compact"
            variant="outlined"
          >
            <v-btn :value="false" size="small">
              <v-icon start>mdi-view-list</v-icon>
              Show All
            </v-btn>
            <v-btn :value="true" size="small">
              <v-icon start>mdi-delta</v-icon>
              Differences Only
            </v-btn>
          </v-btn-toggle>
        </v-col>

        <v-col cols="auto" class="ml-3">
          <v-chip
            color="primary"
            variant="tonal"
            size="small"
            prepend-icon="mdi-compare"
          >
            Comparing {{ trims.length }} trims
          </v-chip>
        </v-col>

        <v-spacer />

        <v-col cols="auto">
          <v-btn
            color="error"
            variant="text"
            size="small"
            @click="$emit('clear-comparison')"
            prepend-icon="mdi-close-circle"
          >
            Clear All
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <v-card elevation="0" class="comparison-table-card">
      <div class="comparison-table-wrapper">
        <table class="comparison-table" role="table">
          <thead>
            <tr role="row">
              <th
                class="feature-header sticky-column"
                scope="col"
                role="columnheader"
                aria-label="Feature name"
              >
                <span class="feature-header-text">Feature</span>
              </th>
              <th
                v-for="trim in trims"
                :key="trim.id"
                class="trim-header"
                scope="col"
                role="columnheader"
                :aria-label="`${trim.trim || trim.styleName} trim details`"
              >
                <div class="trim-header-content">
                  <div class="trim-image-container">
                    <v-img
                      :src="trim.img || noVehicleImage"
                      :alt="`${trim.trim || trim.styleName} image`"
                      height="100"
                      contain
                    >
                      <template v-slot:placeholder>
                        <v-progress-circular indeterminate size="24" />
                      </template>
                    </v-img>
                  </div>
                  <h3 class="trim-name">{{ trim.trim || trim.styleName }}</h3>
                  <p class="trim-price">
                    {{ formatCurrency(trim.baseMsrp || trim.price || 0) }}
                  </p>
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    @click="$emit('remove-trim', trim.id)"
                    :aria-label="`Remove ${trim.trim || trim.styleName} from comparison`"
                  >
                    <v-icon size="small">mdi-close</v-icon>
                  </v-btn>
                </div>
              </th>
            </tr>
          </thead>

          <tbody>
            <template v-for="section in filteredSections" :key="section.key">
              <tr class="section-row" role="row">
                <td
                  :colspan="trims.length + 1"
                  class="section-cell"
                  role="rowheader"
                >
                  <div class="section-title">
                    <v-icon :icon="section.icon" size="small" class="mr-2" />
                    {{ section.title }}
                  </div>
                </td>
              </tr>

              <tr
                v-for="feature in section.features"
                :key="feature.key"
                class="feature-row"
                :class="{ 'has-difference': feature.hasDifference }"
                role="row"
              >
                <td
                  class="feature-name-cell sticky-column"
                  role="rowheader"
                  scope="row"
                >
                  <span class="feature-name">{{ feature.name }}</span>
                  <v-tooltip
                    v-if="feature.tooltip"
                    location="top"
                    :text="feature.tooltip"
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon
                        v-bind="props"
                        size="x-small"
                        class="ml-1"
                        color="grey"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                  </v-tooltip>
                </td>

                <td
                  v-for="(value, index) in feature.values"
                  :key="`${feature.key}-${index}`"
                  class="feature-value-cell"
                  :class="{
                    'is-different':
                      showOnlyDifferences &&
                      feature.hasDifference &&
                      isDifferentFromFirst(feature.values, index),
                    'is-better': feature.isBetter && feature.isBetter[index],
                    'is-worse': feature.isWorse && feature.isWorse[index],
                  }"
                  role="gridcell"
                >
                  <div class="feature-value-content">
                    <template v-if="feature.type === 'boolean'">
                      <v-icon
                        v-if="value === true"
                        color="success"
                        size="small"
                        :aria-label="'Available'"
                      >
                        mdi-check-circle
                      </v-icon>
                      <v-icon
                        v-else-if="value === false"
                        color="grey-lighten-2"
                        size="small"
                        :aria-label="'Not available'"
                      >
                        mdi-minus-circle
                      </v-icon>
                      <span v-else class="text-grey">{{ value || 'N/A' }}</span>
                    </template>

                    <template v-else-if="feature.type === 'rating'">
                      <v-rating
                        :model-value="Number(value) || 0"
                        readonly
                        density="compact"
                        size="small"
                        color="amber"
                      />
                    </template>

                    <template v-else>
                      <span class="feature-value-text">
                        {{ formatFeatureValue(value, feature.format) }}
                      </span>
                    </template>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { formatCurrency, getCDNUrl } from '@/utils'
import { parseSpecItem, parseSpecValue } from '@/utils/modelDetails'
import { tracker } from '@/services/tracking'
import type { TrimData, ModelData } from '@/types'

const props = defineProps<{
  trims: TrimData[]
  modelData?: ModelData
  showDifferencesDefault?: boolean
}>()

const emit = defineEmits<{
  'remove-trim': [trimId: string]
  'clear-comparison': []
  'diff-toggle': [enabled: boolean]
}>()

const cdnUrl = getCDNUrl()
const noVehicleImage = `${cdnUrl}/lightning-model-page/cx/no-vehicle-image.png`

const showOnlyDifferences = ref(props.showDifferencesDefault ?? true)

watch(showOnlyDifferences, newValue => {
  emit('diff-toggle', newValue)
  tracker.trackDiffToggle(newValue)
})

interface Feature {
  key: string
  name: string
  format?: string
  type?: string
  tooltip?: string
  compareNumeric?: boolean
}

interface Section {
  key: string
  title: string
  icon: string
  features: Feature[]
}

const sections: Section[] = [
  {
    key: 'pricing',
    title: 'Pricing & Value',
    icon: 'mdi-currency-usd',
    features: [
      { key: 'msrp', name: 'Starting MSRP', format: 'currency' },
      { key: 'invoice', name: 'Invoice Price', format: 'currency' },
      { key: 'destination', name: 'Destination Fee', format: 'currency' },
    ],
  },
  {
    key: 'performance',
    title: 'Performance',
    icon: 'mdi-speedometer',
    features: [
      { key: 'engine', name: 'Engine Type' },
      { key: 'horsepower', name: 'Horsepower', format: 'hp' },
      { key: 'torque', name: 'Torque', format: 'torque' },
      { key: 'transmission', name: 'Transmission' },
      { key: 'drivetrain', name: 'Drivetrain' },
      { key: 'acceleration', name: '0-60 mph', format: 'seconds' },
    ],
  },
  {
    key: 'fuel',
    title: 'Fuel Economy',
    icon: 'mdi-gas-station',
    features: [
      {
        key: 'city_mpg',
        name: 'City MPG',
        format: 'mpg',
        compareNumeric: true,
      },
      {
        key: 'highway_mpg',
        name: 'Highway MPG',
        format: 'mpg',
        compareNumeric: true,
      },
      {
        key: 'combined_mpg',
        name: 'Combined MPG',
        format: 'mpg',
        compareNumeric: true,
      },
      { key: 'fuel_capacity', name: 'Fuel Tank Capacity', format: 'gallons' },
      { key: 'range', name: 'Estimated Range', format: 'miles' },
    ],
  },
  {
    key: 'dimensions',
    title: 'Dimensions & Capacity',
    icon: 'mdi-car',
    features: [
      { key: 'seating', name: 'Seating Capacity', format: 'seats' },
      { key: 'length', name: 'Length', format: 'inches' },
      { key: 'width', name: 'Width', format: 'inches' },
      { key: 'height', name: 'Height', format: 'inches' },
      { key: 'wheelbase', name: 'Wheelbase', format: 'inches' },
      { key: 'cargo', name: 'Cargo Volume', format: 'cubic_feet' },
      { key: 'front_headroom', name: 'Front Head Room', format: 'inches' },
      { key: 'front_legroom', name: 'Front Leg Room', format: 'inches' },
      { key: 'rear_headroom', name: 'Rear Head Room', format: 'inches' },
      { key: 'rear_legroom', name: 'Rear Leg Room', format: 'inches' },
    ],
  },
  {
    key: 'safety',
    title: 'Safety Features',
    icon: 'mdi-shield-check',
    features: [
      { key: 'airbags', name: 'Airbags', format: 'count' },
      { key: 'abs', name: 'Anti-lock Brakes', type: 'boolean' },
      { key: 'stability_control', name: 'Stability Control', type: 'boolean' },
      { key: 'traction_control', name: 'Traction Control', type: 'boolean' },
      { key: 'blind_spot', name: 'Blind Spot Monitoring', type: 'boolean' },
      {
        key: 'lane_departure',
        name: 'Lane Departure Warning',
        type: 'boolean',
      },
      { key: 'lane_keeping', name: 'Lane Keeping Assist', type: 'boolean' },
      {
        key: 'forward_collision',
        name: 'Forward Collision Warning',
        type: 'boolean',
      },
      {
        key: 'automatic_braking',
        name: 'Automatic Emergency Braking',
        type: 'boolean',
      },
      {
        key: 'adaptive_cruise',
        name: 'Adaptive Cruise Control',
        type: 'boolean',
      },
      { key: 'rear_camera', name: 'Backup Camera', type: 'boolean' },
      { key: 'parking_sensors', name: 'Parking Sensors', type: 'boolean' },
      { key: 'safety_rating', name: 'NHTSA Overall Rating', type: 'rating' },
    ],
  },
  {
    key: 'comfort',
    title: 'Comfort & Convenience',
    icon: 'mdi-seat-recline-extra',
    features: [
      { key: 'climate_zones', name: 'Climate Zones', format: 'zones' },
      { key: 'heated_seats', name: 'Heated Seats', type: 'boolean' },
      { key: 'cooled_seats', name: 'Ventilated Seats', type: 'boolean' },
      { key: 'leather_seats', name: 'Leather Seats', type: 'boolean' },
      { key: 'memory_seats', name: 'Memory Seats', type: 'boolean' },
      { key: 'power_seats', name: 'Power Adjustable Seats', type: 'boolean' },
      {
        key: 'heated_steering',
        name: 'Heated Steering Wheel',
        type: 'boolean',
      },
      { key: 'keyless_entry', name: 'Keyless Entry', type: 'boolean' },
      { key: 'push_start', name: 'Push Button Start', type: 'boolean' },
      { key: 'remote_start', name: 'Remote Start', type: 'boolean' },
      { key: 'sunroof', name: 'Sunroof/Moonroof', type: 'boolean' },
      { key: 'power_liftgate', name: 'Power Liftgate', type: 'boolean' },
    ],
  },
  {
    key: 'technology',
    title: 'Technology & Entertainment',
    icon: 'mdi-monitor-dashboard',
    features: [
      { key: 'display_size', name: 'Display Size', format: 'inches' },
      { key: 'navigation', name: 'Navigation System', type: 'boolean' },
      { key: 'apple_carplay', name: 'Apple CarPlay', type: 'boolean' },
      { key: 'android_auto', name: 'Android Auto', type: 'boolean' },
      { key: 'bluetooth', name: 'Bluetooth', type: 'boolean' },
      { key: 'usb_ports', name: 'USB Ports', format: 'count' },
      { key: 'wireless_charging', name: 'Wireless Charging', type: 'boolean' },
      { key: 'premium_audio', name: 'Premium Audio System', type: 'boolean' },
      { key: 'speakers', name: 'Number of Speakers', format: 'count' },
      { key: 'wifi_hotspot', name: 'Wi-Fi Hotspot', type: 'boolean' },
      { key: 'hud', name: 'Head-Up Display', type: 'boolean' },
    ],
  },
  {
    key: 'warranty',
    title: 'Warranty',
    icon: 'mdi-shield-account',
    features: [
      { key: 'basic_warranty', name: 'Basic Warranty' },
      { key: 'powertrain_warranty', name: 'Powertrain Warranty' },
      { key: 'corrosion_warranty', name: 'Corrosion Warranty' },
      { key: 'roadside_assistance', name: 'Roadside Assistance' },
      { key: 'maintenance', name: 'Complimentary Maintenance' },
    ],
  },
]

const getSpecValue = (
  trim: TrimData,
  category: string,
  searchTerm: string,
): string | number | null => {
  const items = trim.technicalSpecification?.[category]?.items || []
  const item = items.find((i: string) =>
    i.toLowerCase().includes(searchTerm.toLowerCase()),
  )
  if (item) {
    const value = parseSpecValue(item)
    return value !== 'N/A' ? value : null
  }
  return null
}

const getFlexibleValue = (
  trim: TrimData,
  paths: Array<{ category: string; searchTerms: string[] }>,
): string | number | null => {
  for (const path of paths) {
    for (const searchTerm of path.searchTerms) {
      const value = getSpecValue(trim, path.category, searchTerm)
      if (value && value !== 'N/A') return value
    }
  }
  return null
}

const hasFeature = (
  trim: TrimData,
  category: string,
  searchTerm: string,
): boolean => {
  const standardItems = trim.standardEquipment?.[category]?.items || []
  const availableItems = trim.availableOptions?.[category]?.items || []
  const allItems = [...standardItems, ...availableItems]
  return allItems.some((item: string) =>
    item.toLowerCase().includes(searchTerm.toLowerCase()),
  )
}

const getFeatureValues = (
  feature: Feature,
): (string | number | boolean | null)[] => {
  return props.trims.map(trim => {
    const result = (() => {
      switch (feature.key) {
        case 'msrp':
          return trim.baseMsrp || trim.price || null
        case 'invoice':
          return (
            ((trim as Record<string, unknown>).invoicePrice as
              | string
              | number
              | null) || null
          )
        case 'destination':
          return (
            ((trim as Record<string, unknown>).destinationFee as
              | string
              | number
              | null) || null
          )

        case 'engine':
          return (
            getFlexibleValue(trim, [
              {
                category: 'Engine',
                searchTerms: ['Engine Type', 'Type', 'Displacement'],
              },
            ]) ||
            (() => {
              const firstItem = trim.technicalSpecification?.Engine?.items?.[0]
              if (!firstItem) return 'Engine'
              const parsed = parseSpecItem(firstItem, false)
              return typeof parsed === 'object' ? parsed.label : firstItem
            })()
          )
        case 'horsepower':
          return getFlexibleValue(trim, [
            {
              category: 'Engine',
              searchTerms: ['Horsepower', 'HP', 'SAE Net Horsepower'],
            },
          ])
        case 'torque':
          return getFlexibleValue(trim, [
            {
              category: 'Engine',
              searchTerms: ['Torque', 'SAE Net Torque', 'lb-ft'],
            },
          ])
        case 'transmission':
          return getFlexibleValue(trim, [
            {
              category: 'Transmission',
              searchTerms: ['Trans Description', 'Transmission', 'Type'],
            },
          ])
        case 'drivetrain':
          return getFlexibleValue(trim, [
            {
              category: 'Transmission',
              searchTerms: ['Drivetrain', 'Drive Type', 'Drive'],
            },
          ])
        case 'acceleration':
          return getFlexibleValue(trim, [
            {
              category: 'Performance',
              searchTerms: ['0-60', '0 to 60', 'acceleration'],
            },
          ])

        case 'city_mpg':
          const cityValue = getFlexibleValue(trim, [
            { category: 'Mileage', searchTerms: ['EPA MPG City', 'City MPG'] },
          ])
          return cityValue &&
            typeof cityValue === 'string' &&
            !cityValue.includes('mi')
            ? cityValue
            : typeof cityValue === 'number'
              ? cityValue
              : null
        case 'highway_mpg':
          const highwayValue = getFlexibleValue(trim, [
            {
              category: 'Mileage',
              searchTerms: ['EPA MPG Highway', 'Highway MPG'],
            },
          ])
          return highwayValue &&
            typeof highwayValue === 'string' &&
            !highwayValue.includes('mi')
            ? highwayValue
            : typeof highwayValue === 'number'
              ? highwayValue
              : null
        case 'combined_mpg':
          const combinedValue = getFlexibleValue(trim, [
            {
              category: 'Mileage',
              searchTerms: [
                'Fuel Economy Est-Combined',
                'Combined MPG',
                'Combined',
              ],
            },
          ])
          return combinedValue &&
            typeof combinedValue === 'string' &&
            !combinedValue.includes(' mi')
            ? combinedValue
            : typeof combinedValue === 'number'
              ? combinedValue
              : null
        case 'fuel_capacity':
          return getFlexibleValue(trim, [
            {
              category: 'Fuel Tank',
              searchTerms: ['Capacity', 'Fuel Tank Capacity', 'Tank Capacity'],
            },
            { category: 'Mileage', searchTerms: ['Fuel Tank'] },
          ])
        case 'range':
          const rangeValue = getFlexibleValue(trim, [
            {
              category: 'Mileage',
              searchTerms: ['Range', 'Est Range', 'Estimated Range'],
            },
          ])
          return rangeValue && typeof rangeValue === 'string'
            ? rangeValue.replace(/\s+(mi|miles)\s+(mi|miles)/, ' $1')
            : rangeValue

        case 'seating':
          return getFlexibleValue(trim, [
            {
              category: 'Interior Dimensions',
              searchTerms: ['Passenger', 'Capacity', 'Seating'],
            },
            { category: 'Summary', searchTerms: ['Passenger', 'Seating'] },
          ])
        case 'length':
          return getFlexibleValue(trim, [
            {
              category: 'Exterior Dimensions',
              searchTerms: ['Length', 'Overall Length'],
            },
          ])
        case 'width':
          return getFlexibleValue(trim, [
            {
              category: 'Exterior Dimensions',
              searchTerms: ['Width', 'Overall Width'],
            },
          ])
        case 'height':
          return getFlexibleValue(trim, [
            {
              category: 'Exterior Dimensions',
              searchTerms: ['Height', 'Overall Height'],
            },
          ])
        case 'wheelbase':
          return getFlexibleValue(trim, [
            {
              category: 'Exterior Dimensions',
              searchTerms: ['Wheelbase', 'Wheel Base'],
            },
          ])
        case 'cargo':
          return getFlexibleValue(trim, [
            {
              category: 'Interior Dimensions',
              searchTerms: ['Cargo', 'Cargo Volume', 'Cargo Space'],
            },
          ])
        case 'front_headroom':
          return getFlexibleValue(trim, [
            {
              category: 'Interior Dimensions',
              searchTerms: ['Front Head', 'Head Room'],
            },
          ])
        case 'front_legroom':
          return getFlexibleValue(trim, [
            {
              category: 'Interior Dimensions',
              searchTerms: ['Front Leg', 'Leg Room'],
            },
          ])
        case 'rear_headroom':
          return getFlexibleValue(trim, [
            {
              category: 'Interior Dimensions',
              searchTerms: ['Rear Head', '2nd Row Head'],
            },
          ])
        case 'rear_legroom':
          return getFlexibleValue(trim, [
            {
              category: 'Interior Dimensions',
              searchTerms: ['Rear Leg', '2nd Row Leg'],
            },
          ])

        case 'abs':
          return hasFeature(trim, 'SAFETY', 'Anti-lock Brakes')
        case 'stability_control':
          return hasFeature(trim, 'SAFETY', 'Stability Control')
        case 'traction_control':
          return hasFeature(trim, 'SAFETY', 'Traction Control')
        case 'blind_spot':
          return (
            hasFeature(trim, 'SAFETY', 'Blind Spot') ||
            hasFeature(trim, 'SAFETY', 'BLIS')
          )
        case 'lane_departure':
          return hasFeature(trim, 'SAFETY', 'Lane Departure')
        case 'lane_keeping':
          return hasFeature(trim, 'SAFETY', 'Lane Keeping')
        case 'forward_collision':
          return hasFeature(trim, 'SAFETY', 'Forward Collision')
        case 'automatic_braking':
          return hasFeature(trim, 'SAFETY', 'Automatic Emergency')
        case 'adaptive_cruise':
          return hasFeature(trim, 'SAFETY', 'Adaptive Cruise')
        case 'rear_camera':
          return (
            hasFeature(trim, 'SAFETY', 'Backup Camera') ||
            hasFeature(trim, 'SAFETY', 'Rear Camera')
          )
        case 'parking_sensors':
          return hasFeature(trim, 'SAFETY', 'Parking Sensors')
        case 'airbags':
          return getSpecValue(trim, 'Safety', 'Airbags')
        case 'safety_rating':
          return trim.safetyRating || null

        case 'climate_zones':
          return getSpecValue(trim, 'COMFORT', 'Climate Control')
        case 'heated_seats':
          return (
            hasFeature(trim, 'COMFORT', 'Heated Seats') ||
            hasFeature(trim, 'INTERIOR', 'Heated Seats')
          )
        case 'cooled_seats':
          return (
            hasFeature(trim, 'COMFORT', 'Cooled Seats') ||
            hasFeature(trim, 'COMFORT', 'Ventilated Seats')
          )
        case 'leather_seats':
          return (
            hasFeature(trim, 'INTERIOR', 'Leather') ||
            hasFeature(trim, 'COMFORT', 'Leather')
          )
        case 'memory_seats':
          return hasFeature(trim, 'COMFORT', 'Memory Seats')
        case 'power_seats':
          return (
            hasFeature(trim, 'COMFORT', 'Power Adjustable') ||
            hasFeature(trim, 'COMFORT', 'Power Seats')
          )
        case 'heated_steering':
          return hasFeature(trim, 'COMFORT', 'Heated Steering')
        case 'keyless_entry':
          return hasFeature(trim, 'COMFORT', 'Keyless Entry')
        case 'push_start':
          return hasFeature(trim, 'COMFORT', 'Push Button Start')
        case 'remote_start':
          return hasFeature(trim, 'COMFORT', 'Remote Start')
        case 'sunroof':
          return (
            hasFeature(trim, 'EXTERIOR', 'Sunroof') ||
            hasFeature(trim, 'EXTERIOR', 'Moonroof')
          )
        case 'power_liftgate':
          return hasFeature(trim, 'EXTERIOR', 'Power Liftgate')

        case 'display_size':
          return getSpecValue(trim, 'TECHNOLOGY', 'Display Size')
        case 'navigation':
          return hasFeature(trim, 'TECHNOLOGY', 'Navigation')
        case 'apple_carplay':
          return hasFeature(trim, 'TECHNOLOGY', 'Apple CarPlay')
        case 'android_auto':
          return hasFeature(trim, 'TECHNOLOGY', 'Android Auto')
        case 'bluetooth':
          return hasFeature(trim, 'TECHNOLOGY', 'Bluetooth')
        case 'usb_ports':
          return getSpecValue(trim, 'TECHNOLOGY', 'USB Ports')
        case 'wireless_charging':
          return hasFeature(trim, 'TECHNOLOGY', 'Wireless Charging')
        case 'premium_audio':
          return (
            hasFeature(trim, 'TECHNOLOGY', 'Premium Audio') ||
            hasFeature(trim, 'TECHNOLOGY', 'Premium Sound')
          )
        case 'speakers':
          return getSpecValue(trim, 'TECHNOLOGY', 'Speakers')
        case 'wifi_hotspot':
          return (
            hasFeature(trim, 'TECHNOLOGY', 'Wi-Fi') ||
            hasFeature(trim, 'TECHNOLOGY', 'Hotspot')
          )
        case 'hud':
          return hasFeature(trim, 'TECHNOLOGY', 'Head-Up Display')

        case 'basic_warranty':
          return trim.warranty?.basic || getSpecValue(trim, 'Warranty', 'Basic')
        case 'powertrain_warranty':
          return (
            trim.warranty?.powertrain ||
            getSpecValue(trim, 'Warranty', 'Powertrain')
          )
        case 'corrosion_warranty':
          return (
            trim.warranty?.corrosion ||
            getSpecValue(trim, 'Warranty', 'Corrosion')
          )
        case 'roadside_assistance':
          return (
            trim.warranty?.roadside ||
            getSpecValue(trim, 'Warranty', 'Roadside')
          )
        case 'maintenance':
          return (
            trim.warranty?.maintenance ||
            getSpecValue(trim, 'Warranty', 'Maintenance')
          )

        default:
          return null
      }
    })()
    if (result === undefined || result === null) {
      return null
    }
    if (
      typeof result === 'string' ||
      typeof result === 'number' ||
      typeof result === 'boolean'
    ) {
      return result
    }
    if (typeof result === 'object' && result !== null) {
      return null
    }
    return null
  }) as (string | number | boolean | null)[]
}

const isDifferentFromFirst = (
  values: (string | number | boolean | null)[],
  index: number,
): boolean => {
  if (index === 0) return false
  const firstValue = values[0]
  const currentValue = values[index]

  if (firstValue == null && currentValue == null) return false
  if (firstValue == null || currentValue == null) return true

  return firstValue !== currentValue
}

const areAllValuesSame = (
  values: (string | number | boolean | null)[],
): boolean => {
  if (values.length <= 1) return true

  const nonNullValues = values.filter(v => v != null && v !== '')

  if (nonNullValues.length === 0) return true

  if (nonNullValues.length !== values.length) return false

  const firstValue = nonNullValues[0]
  const firstStr =
    typeof firstValue === 'string'
      ? firstValue.toLowerCase().trim()
      : String(firstValue)

  return nonNullValues.every(v => {
    const vStr = typeof v === 'string' ? v.toLowerCase().trim() : String(v)
    return vStr === firstStr
  })
}

const formatFeatureValue = (
  value: string | number | boolean | null | undefined,
  format?: string,
): string => {
  if (value == null || value === '') return 'N/A'

  let cleanValue = String(value).trim()
  cleanValue = cleanValue
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  switch (format) {
    case 'currency':
      return formatCurrency(
        typeof value === 'number' ? value : parseFloat(cleanValue) || 0,
      )
    case 'mpg':
      if (cleanValue.toLowerCase().includes('mpg')) return cleanValue
      return `${cleanValue} mpg`
    case 'hp':
      if (cleanValue.toLowerCase().includes('hp')) return cleanValue
      return `${cleanValue} hp`
    case 'torque':
      if (cleanValue.toLowerCase().includes('lb-ft')) return cleanValue
      return `${cleanValue} lb-ft`
    case 'seconds':
      return `${cleanValue}s`
    case 'gallons':
      if (cleanValue.toLowerCase().includes('gal')) return cleanValue
      return `${cleanValue} gal`
    case 'miles':
      cleanValue = cleanValue.replace(/\s+(mi|miles)\s+(mi|miles)/gi, ' $1')
      if (cleanValue.toLowerCase().includes('mi')) return cleanValue
      return `${cleanValue} mi`
    case 'inches':
      if (cleanValue.includes('"') || cleanValue.toLowerCase().includes('in'))
        return cleanValue
      return `${cleanValue}"`
    case 'cubic_feet':
      if (
        cleanValue.toLowerCase().includes('cu ft') ||
        cleanValue.toLowerCase().includes('cubic')
      )
        return cleanValue
      return `${cleanValue} cu ft`
    case 'seats':
      if (cleanValue.toLowerCase().includes('seat')) return cleanValue
      return `${cleanValue} seats`
    case 'zones':
      return `${cleanValue}-zone`
    case 'count':
      return cleanValue
    default:
      return cleanValue
  }
}

const filteredSections = computed(() => {
  return sections
    .map(section => {
      const features = section.features.map(feature => {
        const values = getFeatureValues(feature)
        const hasDifference = !areAllValuesSame(values)

        const hasAnyValue = values.some(
          v => v != null && v !== '' && v !== 'N/A',
        )

        let isBetter: boolean[] = []
        let isWorse: boolean[] = []

        if (feature.compareNumeric && hasDifference && hasAnyValue) {
          const numericValues = values.map(v => {
            if (v == null) return 0
            const parsed =
              typeof v === 'string'
                ? parseFloat(v.replace(/[^0-9.]/g, ''))
                : typeof v === 'number'
                  ? v
                  : parseFloat(String(v))
            return isNaN(parsed) ? 0 : parsed
          })
          const validValues = numericValues.filter(v => v > 0)

          if (validValues.length > 0) {
            const maxValue = Math.max(...validValues)
            const minValue = Math.min(...validValues)

            isBetter = numericValues.map(
              v => v > 0 && v === maxValue && maxValue !== minValue,
            )
            isWorse = numericValues.map(
              v => v > 0 && v === minValue && maxValue !== minValue,
            )
          }
        }

        return {
          ...feature,
          values,
          hasDifference,
          hasAnyValue,
          isBetter,
          isWorse,
        }
      })

      const filteredFeatures = features.filter(f => {
        if (!f.hasAnyValue) return false

        if (showOnlyDifferences.value && !f.hasDifference) return false

        return true
      })

      return {
        ...section,
        features: filteredFeatures,
      }
    })
    .filter(section => section.features.length > 0)
})
</script>

<style scoped>
.enhanced-trim-comparison {
  width: 100%;
}

.comparison-controls {
  padding: 16px;
  background: white;
  border-radius: 8px 8px 0 0;
  border: 1px solid #e0e0e0;
  border-bottom: none;
}

.comparison-table-card {
  border-radius: 0 0 8px 8px;
}

.comparison-table-wrapper {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 80vh;
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 8px 8px;
}

.comparison-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: white;
}

.sticky-column {
  position: sticky;
  left: 0;
  z-index: 2;
  background: white;
  border-right: 2px solid #e0e0e0;
}

.feature-header,
.trim-header {
  padding: 16px;
  background: #f5f5f5;
  font-weight: 600;
  text-align: left;
  border-bottom: 2px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 1;
}

.feature-header {
  z-index: 3;
  min-width: 200px;
}

.trim-header {
  min-width: 250px;
  text-align: center;
  background: white;
}

.feature-header-text {
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #616161;
}

.trim-header-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.trim-image-container {
  width: 100%;
  max-width: 180px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  border-radius: 4px;
  overflow: hidden;
}

.trim-name {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.trim-price {
  font-size: 1.25rem;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  margin: 0;
}

.section-row {
  background: #f8f9fa;
}

.section-cell {
  padding: 12px 16px;
  font-weight: 600;
  background: linear-gradient(to right, #f1f3f5, #f8f9fa);
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #495057;
}

.feature-row {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.feature-row:hover {
  background-color: #f8f9fa;
}

.feature-row.has-difference {
  background-color: rgba(25, 118, 210, 0.02);
}

.feature-name-cell {
  padding: 12px 16px;
  font-weight: 500;
  color: #2c3e50;
}

.feature-name {
  font-size: 0.875rem;
}

.feature-value-cell {
  padding: 12px 16px;
  text-align: center;
  font-size: 0.875rem;
  color: #495057;
  border-left: 1px solid #f0f0f0;
  transition: all 0.2s ease;
  max-width: 300px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
}

.feature-value-cell.is-different {
  background-color: rgba(25, 118, 210, 0.08);
  font-weight: 600;
  color: #1a1a1a;
}

.feature-value-cell.is-better {
  background-color: rgba(76, 175, 80, 0.08);
  color: #2e7d32;
}

.feature-value-cell.is-worse {
  background-color: rgba(244, 67, 54, 0.08);
  color: #c62828;
}

.feature-value-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.feature-value-text {
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  display: inline-block;
}

@media (max-width: 960px) {
  .trim-header {
    min-width: 200px;
  }

  .trim-image-container {
    height: 80px;
    max-width: 140px;
  }

  .trim-name {
    font-size: 0.875rem;
  }

  .trim-price {
    font-size: 1rem;
  }
}

@media (max-width: 600px) {
  .feature-header,
  .feature-name-cell {
    min-width: 150px;
  }

  .trim-header {
    min-width: 160px;
  }

  .trim-image-container {
    height: 60px;
    max-width: 100px;
  }

  .feature-name {
    font-size: 0.8125rem;
  }

  .feature-value-cell {
    font-size: 0.8125rem;
    padding: 10px 12px;
  }
}

.v-theme--dark .enhanced-trim-comparison {
  .comparison-controls,
  .comparison-table,
  .sticky-column {
    background: #1e1e1e;
  }

  .feature-header,
  .trim-header {
    background: #2d2d2d;
    border-color: #424242;
  }

  .section-cell {
    background: linear-gradient(to right, #2d2d2d, #1e1e1e);
  }

  .feature-row:hover {
    background-color: #2d2d2d;
  }

  .feature-row.has-difference {
    background-color: rgba(25, 118, 210, 0.1);
  }

  .feature-value-cell.is-different {
    background-color: rgba(25, 118, 210, 0.2);
  }

  .feature-value-cell {
    border-color: #424242;
  }

  .sticky-column {
    border-color: #424242;
  }
}

.comparison-table:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.feature-row:focus-within {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
}

@media print {
  .comparison-controls {
    display: none;
  }

  .comparison-table-wrapper {
    max-height: none;
    overflow: visible;
  }

  .feature-header,
  .trim-header {
    position: static;
  }
}
</style>
